import { PayloadAction } from '@reduxjs/toolkit';
import {
  AuthSelectors,
  ConfigActions,
  ConfigSelectors,
  HistogramActions,
  HistogramSelectors,
  ProgressActions,
  ProgressSelectors,
  StatsActions,
  StatsSelectors,
  getWorkoutId,
  preloadAgeGroupSaga,
  preloadStatsSaga,
} from '@web_opxp/features';
import { all, call, delay, fork, put, select, spawn, take, takeEvery } from 'redux-saga/effects';
import { DEFAULT_CATEGORY_ID, TRAINING_DOMAINS } from '../base/constants';
import { EDomain, EExerciseCategory } from '../base/types';
import { makeExerciseCategoryStatString } from '../base/utils';
import { openExerciseSelectionAction } from './firebaseActions';
import * as Selectors from './selectors';
import { ExercisesActions } from './slice';
import { IExercise } from './types';

export function* waitForComparison(domainId: EDomain, workoutId: number, siblings: number[], userId?: number) {
  let loaded = false;
  while (!loaded) {
    const siblingsProgressLoaded = yield select(ProgressSelectors.isLastProgressLoadedForWorkouts, siblings, userId);
    const histogramLoaded = yield select(HistogramSelectors.isWorkoutHistogramLoaded, workoutId, userId);
    loaded = siblingsProgressLoaded && histogramLoaded;
    if (!loaded) {
      // wait a little bit
      yield delay(300);
    }
  }
  // get existing state
  const exercise: IExercise = yield select(Selectors.getExerciseFromDomainByWorkoutId, domainId, workoutId, userId);
  // calculate comparison as now we should have all necessary data
  const score: number = yield select(ProgressSelectors.getWorkoutLastScoreWithSiblings, siblings, userId);
  const comparison = yield select(HistogramSelectors.getWorkoutComparison, workoutId, score, userId);
  // update comparison
  const updatedExercise: IExercise = {
    ...exercise,
    comparison,
  };
  return updatedExercise;
}

export function* loadExercisesForDomain(
  domainId: EDomain,
  workoutIds: number[],
  siblingsIds: number[][],
  userId?: number
) {
  const promiseBuffer: Array<Promise<any>> = [];
  for (let index = 0; index < workoutIds.length; index++) {
    const workoutId = workoutIds[index];
    const siblings = siblingsIds[index];
    // wait for receiving the data and calculate the comparison
    const task = yield fork(waitForComparison, domainId, workoutId, siblings, userId);
    const promise = task.toPromise();
    promiseBuffer.push(promise);
  }
  const result: IExercise[] = yield Promise.all(promiseBuffer);
  yield put(ExercisesActions.updateDomain(domainId, result));
}

export function* waitForConfig() {
  const configIsLoading = yield select(ConfigSelectors.isConfigLoading);
  if (configIsLoading) {
    yield take(ConfigActions.configReady);
  }
}

export function* preloadStatsForUser(userId?: number) {
  // wait for stats feature if not ready yet
  yield call(preloadStatsSaga);
  if (!userId) {
    // for default user stats are loaded at this point
    return;
  }
  // for custom user we need to load stats explicitly
  yield put(StatsActions.loadStats(userId));
  yield take(StatsActions.statsReady);
}

export function* initExercisesSaga(action: PayloadAction<number | undefined>) {
  const userId = action.payload;
  // get user premium status
  const isUserPremium = yield select(AuthSelectors.isPremium);
  // check if user is moderator
  const isModerator: boolean = yield select(AuthSelectors.isModerator);
  // wait for config if not ready yet
  yield call(waitForConfig);
  // preload stats if needed
  yield call(preloadStatsForUser, userId);
  // generate full workouts list
  const workoutIds: number[] = [];
  const siblingsIds: number[] = [];
  for (const domainId of TRAINING_DOMAINS) {
    const exercises: IExercise[] = [];
    const domainWorkoutIds: number[] = [];
    const domainSiblingIds: number[][] = [];
    const exerciseIds = yield select(ConfigSelectors.getDomainExerciseIds, domainId);
    for (const exerciseId of exerciseIds) {
      // for each exerciseId find proper workoutId
      const categoryGroupId: number = yield select(ConfigSelectors.getCategoryGroupId, exerciseId);
      const categoryIdStat: EExerciseCategory | undefined = yield select(
        StatsSelectors.getStatIntValueById,
        makeExerciseCategoryStatString(categoryGroupId),
        userId
      );
      // if stat is not set use default category (normal difficulty)
      const categoryId = categoryIdStat ?? DEFAULT_CATEGORY_ID;
      const workoutId = getWorkoutId(categoryId, categoryGroupId);
      const isDisabled = yield select(StatsSelectors.getStatBooleanValueById, `9-4-${workoutId}`, userId);
      const isReleased = yield select(ConfigSelectors.isWorkoutReleasedForWeb, workoutId);
      if (workoutIds.includes(workoutId) || isDisabled || !isReleased) {
        // skip repeating workout ids (as challenge workouts), if its disabled by user or if its not released for web platform
        continue;
      }
      const siblings: number[] = yield select(ConfigSelectors.getWorkoutIdsOfCategoryGroup, workoutId);
      const isPremium = yield select(ConfigSelectors.isWorkoutPremium, workoutId);
      let balancingJson: string | undefined;
      if (isModerator) {
        balancingJson = yield select(ConfigSelectors.getExerciseJsonFile, workoutId);
      }
      // put initial info in state to start rendering
      const initialExercise: IExercise = {
        exerciseId,
        comparison: null,
        locked: isPremium && !isUserPremium,
        workoutId,
        siblings,
        balancingJson,
      };
      exercises.push(initialExercise);
      // add found id to the list
      domainWorkoutIds.push(workoutId);
      workoutIds.push(workoutId);
      // add siblings to the full list
      siblingsIds.push(...siblings);
      domainSiblingIds.push(siblings);
    }
    // update domain with loading state of exercises
    yield put(ExercisesActions.updateDomain(domainId, exercises, userId));
    // start loading exercises in the background
    yield spawn(loadExercisesForDomain, domainId, domainWorkoutIds, domainSiblingIds, userId);
  }
  // request fetching last progress and histogram data
  // progress must be loaded for siblings too
  yield put(ProgressActions.loadLastProgress(siblingsIds, userId));
  yield call(preloadAgeGroupSaga, userId);
  const ageGroupId = yield select(StatsSelectors.getAgeGroup, userId);
  yield put(HistogramActions.loadHistogram(workoutIds, ageGroupId, userId));
}

export function* openExercisesPageSaga() {
  // fire firebase event that page was open
  yield put(openExerciseSelectionAction());
  // restart feature
  yield put(ExercisesActions.initExercises());
}

export default function* watchExercises() {
  yield all([
    takeEvery(ExercisesActions.initExercises, initExercisesSaga),
    takeEvery(ExercisesActions.openExercises, openExercisesPageSaga),
  ]);
}
